import { useMemo } from 'react';

import type { ContextIdentifierProvider } from '@atlaskit/editor-common/provider-factory';

import type { SnippetScope } from '@confluence/fabric-media-support/entry-points/snippetScope.ts';

/**
 * Wraps the base contextIdentifierProvider to encode { snippetId, scope }
 * as JSON in objectId, so downstream media code (getSnippetContextId /
 * getAuthFromContext) can identify snippet media nodes and skip the
 * media-token fetch for GLOBAL scope snippets.
 *
 * When no snippetId is provided (e.g. Create Snippet), returns the base
 * provider unchanged so media nodes behave like regular page media.
 */
export function useSnippetEditorContextIdentifierProvider(
	baseProvider: Promise<ContextIdentifierProvider> | undefined,
	snippetId?: string,
	scope?: SnippetScope,
): Promise<ContextIdentifierProvider> | undefined {
	return useMemo(() => {
		if (!baseProvider || !snippetId) {
			return baseProvider;
		}

		return baseProvider.then((base) => ({
			...base,
			objectId: JSON.stringify({ snippetId, scope }),
		}));
	}, [baseProvider, snippetId, scope]);
}
